import React, { useState, useMemo, useEffect } from 'react';
import { FaFileCsv } from 'react-icons/fa';
import { Pagination } from '@/components/Pagination';
import { TableBody } from '@/components/tableBody';
import { ColumnsInterface, SortConfigIn, CustomTableProps } from '@interface';
import { filterData } from '@mockData';
import _ from 'lodash';
import { getDateInterval } from '@utils';
export const CustomTable: React.FC<CustomTableProps> = ({
	data: initialData = [],
	headerClass = '',
	selectTab = '',
	dataInterval = '',
}) => {
	// 1. All state declarations first
	const [column, setColumn] = useState<ColumnsInterface[]>([
		{ key: '', label: '' },
	]);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [sortConfig, setSortConfig] = useState<SortConfigIn>({
		key: null,
		direction: 'asc',
	});
	const [filterList, setFilterList] = useState<Array<Record<string, any>>>([]);
	const [filterType, setFilterType] = useState('all');

	// 2. Effects
	useEffect(() => {
		setFilterType('all');
		if (initialData && initialData.length > 0) {
			if (selectTab) {
				const columns: ColumnsInterface[] = Object.keys(initialData[0]).map(
					(key) => ({
						key,
						label: key,
					})
				);
				setColumn(columns);
			} else {
				const columns: ColumnsInterface[] = Object.keys(initialData[0])
					.filter((key) => {
						const itemInterval = getDateInterval(key);
						if (itemInterval === 'all') {
							if (key === 'latitude' || key === 'longitude')
								return false;
							return true;
						}
						return !itemInterval || itemInterval === dataInterval;
					})
					.map((key) => ({
						key,
						label: key,
					}));
				setColumn(columns);
			}
		}

		selectTab &&
			setFilterList(filterData[selectTab as keyof typeof filterData]);
	}, [initialData, dataInterval]);

	// 3. Memoized values
	const filteredData = useMemo(() => {
		if (filterType === 'all') return initialData;

		const filterFunctions: Record<string, (item: any) => boolean> = {
			salesAndFinancing: (item) => {
				const mortgageRelatedTypes = [
					'M&CON',
					'AGREEMENT',
					'AALR',
					'AL&R',
					'SAT',
					'ASST',
					'MORTGAGE',
				];
				if (filterType === 'mortgage')
					return mortgageRelatedTypes.includes(item['Document Type']);
				if (filterType === 'deed') return item['Document Type'] === 'DEED';
				return false;
			},
			hpdComplaints: (item) => item['Complaint Status'].includes(filterType),
			hpdViolations: (item) => {
				if (['Open', 'Closed'].includes(filterType))
					return item['Violation Status'].includes(filterType);
				if (['A', 'B', 'C'].includes(filterType))
					return item['Class'].includes(filterType);
				return true;
			},
			dobComplaints: (item) =>
				item['Status'].toLowerCase().includes(filterType.toLowerCase()),
			dobViolations: (item) =>
				item['Status'].toLowerCase().includes(filterType.toLowerCase()),
			ecbViolations: (item) =>
				item['Status'].toLowerCase().includes(filterType.toLowerCase()),
			dobFiledPermits: (item) => item['Job Type']?.includes(filterType),
			dobIssuedPermits: (item) => item['Job Type']?.includes(filterType),
		};

		return initialData.filter(filterFunctions[selectTab] || (() => true));
	}, [initialData, filterType, selectTab]);
	// Added selectTab to dependencies

	const sortedData = useMemo(() => {
		if (!sortConfig.key) return filteredData;
		return _.orderBy(
			filteredData,
			[(item: any) => _.get(item, sortConfig.key || '', '')],
			[sortConfig.direction]
		);
	}, [filteredData, sortConfig]);

	const paginatedData = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		return sortedData.slice(startIndex, startIndex + itemsPerPage);
	}, [sortedData, currentPage, itemsPerPage]);

	const pageNumbers = useMemo(() => {
		const totalPages = Math.ceil(sortedData.length / itemsPerPage);
		const pages = [];
		for (let i = 1; i <= totalPages; i++) {
			pages.push(i);
		}
		return pages;
	}, [sortedData.length, itemsPerPage]);

	// 4. Event handlers
	const requestSort = (key: string) => {
		let direction: 'asc' | 'desc' = 'asc';
		if (sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
	};

	const exportCSV = () => {
		if (sortedData.length === 0) return;
		const headers = Object.keys(sortedData[0]).join(',');
		const csvData = sortedData
			.map((row) => Object.values(row).join(','))
			.join('\n');
		const blob = new Blob([`${headers}\n${csvData}`], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'export.csv';
		a.click();
		window.URL.revokeObjectURL(url);
	};
	

	// 5. Conditional render for no data
	if (!initialData || initialData.length === 0) {
		return (
			<div className="w-full p-4">
				<div className="text-center py-8 font-bold text-[1.25rem]">No data available</div>
			</div>
		);
	}

	// 6. Main render
	return (
		<div className="w-full py-4">
			{/* Header Controls */}
			<div
				className={`w-full min-w-max ${headerClass} text-[#324B69] relative`}
			>
				<div className="flex flex-nowrap content-start box-border items-center gap-4 py-[5px] px-[10px]">
					<span className="text-sm">Total: {sortedData.length}</span>
					<div className="flex items-center mr-[4px]">
						<div className="flex flex-wrap font-bold-400">
							{filterList.map((val) => (
								<button
									key={val.label}
									onClick={() => setFilterType(val.value)}
									className={`mr-[5px] px-[.5rem] py-[.25rem] min-w-[60px] max-w-[100px] leading-normal text-[.875rem] inline-block text-center align-middle rounded btn-light ${
										filterType === val.value
											? 'bg-[#324B69] text-white'
											: 'bg-white border'
									}`}
								>
									{val.label}
								</button>
							))}
						</div>
					</div>
					<div className="flex items-center text-[.875rem]">
						<button
							onClick={exportCSV}
							className="btn-primary btn-dark hover:underline flex items-center mr-[10px] space-x-1"
						>
							<span>EXPORT CSV</span>
							<FaFileCsv />
						</button>
						<select
							value={itemsPerPage}
							onChange={(e) => {
								setItemsPerPage(Number(e.target.value));
								setCurrentPage(1);
							}}
							className="btn-primary"
						>
							<option value={10}>10</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
							<option value={500}>500</option>

						</select>
					</div>
				</div>
			</div>

			{/* Table */}
			<TableBody
				paginatedData={paginatedData}
				sortConfig={sortConfig}
				columns={column}
				requestSort={requestSort}
				selectTab={selectTab}
			/>

			{/* Pagination */}
			<Pagination
				currentPage={currentPage}
				totalPages={Math.ceil(sortedData.length / itemsPerPage)}
				pageNumbers={pageNumbers}
				setCurrentPage={setCurrentPage}
			/>
		</div>
	);
};
